import { TransactionStatus } from "./enums";

function parseLocalizedLabel(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    return (
      value.en ||
      value.EN ||
      value.ar ||
      value.METHOD_NAME ||
      value.NAME ||
      null
    );
  }
  const text = String(value).trim();
  if (!text) return null;
  if (text.startsWith("{")) {
    try {
      const obj = JSON.parse(text);
      if (obj && typeof obj === "object") {
        return obj.en || obj.EN || obj.ar || Object.values(obj)[0] || text;
      }
    } catch (_) {
      return text;
    }
  }
  return text;
}

function pickNumber(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function pickText(...values) {
  for (const value of values) {
    const parsed = parseLocalizedLabel(value);
    if (parsed) return String(parsed);
  }
  return null;
}

function walletCurrency(wallet) {
  if (!wallet || typeof wallet !== "object") return null;
  const currency =
    wallet.currencies ||
    wallet.CURRENCY ||
    wallet.currency ||
    null;
  return currency && typeof currency === "object" ? currency : null;
}

function walletLabelFromCurrency(currency) {
  const code = currency?.CURRENCY_CODE || currency?.currencyCode || currency?.CODE || currency?.code;
  return code ? `${code} wallet` : null;
}

function extractWalletUserName(wallet) {
  if (!wallet || typeof wallet !== "object") return null;
  const account = wallet.account || wallet.ACCOUNT || null;
  const user =
    account?.user_login ||
    account?.USER_LOGIN ||
    account?.user ||
    account?.USER ||
    wallet.user ||
    wallet.USER ||
    null;
  return pickText(
    user?.NICK_NAME,
    user?.nickName,
    user?.USER_NAME,
    user?.userName,
    user?.FULL_NAME,
    user?.fullName
  );
}

/**
 * Map a pending cash-in / cash-out request into the view model used by
 * TransactionDetailsModal (same popup as transaction history).
 */
export function mapCashRequestToTransactionDetails(
  req,
  {
    typeCode,
    typeClass,
    typeLabel,
    statusLabel,
    currentUserName,
  } = {}
) {
  if (!req) {
    return { detailsTx: null, detailsData: null };
  }

  const raw = req.raw && typeof req.raw === "object" ? req.raw : {};
  const id = req.requestToken || req.id || raw.REQUEST_TOKEN || raw.TRANSACTION_ID || "-";
  const amount = pickNumber(req.amount, raw.AMOUNT, raw.amount, raw.VALUE);
  const date =
    req.date ||
    raw.UPDATED_AT ||
    raw.REQUEST_DATE ||
    raw.CREATED_AT ||
    raw.DATE ||
    raw.date ||
    null;

  const requesterWallet =
    raw.requester_wallet ||
    raw.REQUESTER_WALLET ||
    raw.wallet ||
    raw.sender_wallet ||
    raw.SENDER_WALLET ||
    raw.receiver_wallet ||
    raw.RECEIVER_WALLET ||
    null;
  const currencyObj =
    raw.currencies ||
    raw.CURRENCY ||
    raw.currency ||
    walletCurrency(requesterWallet);
  const currencySymbol =
    req.currencySymbol ||
    currencyObj?.CURRENCY_SYMBOL ||
    currencyObj?.currencySymbol ||
    currencyObj?.SYMBOL ||
    currencyObj?.symbol ||
    raw.CURRENCY_SYMBOL ||
    null;
  const currencyCode =
    req.currencyCode ||
    currencyObj?.CURRENCY_CODE ||
    currencyObj?.currencyCode ||
    currencyObj?.CODE ||
    currencyObj?.code ||
    raw.CURRENCY_CODE ||
    null;

  const methodObject =
    raw.payment_methods ||
    raw.PAYMENT_METHOD ||
    raw.PaymentMethod ||
    raw.cashin_method ||
    raw.cashout_method ||
    null;
  const paymentMethod =
    pickText(
      req.methodLabel,
      raw.METHOD_LABEL,
      raw.METHOD_NAME,
      raw.METHOD,
      raw.method,
      raw.PAYMENT_METHOD_LABEL,
      raw.PAYMENT_METHOD_NAME,
      methodObject?.METHOD_LABEL,
      methodObject?.METHOD_NAME,
      methodObject?.NAME
    ) ||
    null;

  const adminFees = pickNumber(raw.ADMIN_FEES, raw.admin_fees, raw.adminFees);
  const agentFees = pickNumber(raw.AGENT_FEES, raw.agent_fees, raw.agentFees);
  const totalFees = (adminFees || 0) + (agentFees || 0);
  const amountAfterFees =
    amount != null ? amount - totalFees : pickNumber(raw.AMOUNT_AFTER_FEES, raw.amountAfterFees);

  const walletLabel =
    req.walletLabel ||
    pickText(raw.WALLET_LABEL, raw.WALLET_NAME) ||
    walletLabelFromCurrency(currencyObj);

  const requesterUserName =
    extractWalletUserName(requesterWallet) ||
    pickText(currentUserName) ||
    null;
  const isCashIn = typeClass === "type-cashin";
  const isCashOut = typeClass === "type-cashout";

  const detailsTx = {
    id,
    typeCode,
    typeClass,
    typeLabel,
    statusCode: TransactionStatus.PENDING,
    statusLabel,
    date,
    amount,
    raw,
  };

  const detailsData = {
    id,
    typeCode,
    typeClass,
    typeLabel,
    statusCode: TransactionStatus.PENDING,
    statusLabel: statusLabel || req.statusLabel || pickText(raw.STATUS_LABEL, raw.STATUS_NAME) || "Pending",
    amount,
    adminFees,
    agentFees,
    amountAfterFees,
    currencySymbol,
    currencyCode,
    paymentMethod,
    transactionDate: date,
    senderName: isCashOut ? requesterUserName : null,
    receiverName: isCashIn ? requesterUserName : null,
    senderWalletLabel: isCashOut && !requesterUserName ? walletLabel : null,
    receiverWalletLabel: isCashIn && !requesterUserName ? walletLabel : null,
    raw,
  };

  return { detailsTx, detailsData };
}
